import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonWrapper } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    desc = "desc",
    link = "link",
    label = "label",
    cssClass = "cssClass",
    openInNewWidow = "openInNewWidow",
    emailInfo = "emailInfo",
    backgroundColor = "backgroundColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.cssClass]: string;
        [fields.desc]: string;
        [fields.link]: LinkFieldValue;
        [fields.label]: string;
        [fields.emailInfo]: string;
        [fields.openInNewWidow]: boolean;
        [fields.backgroundColor]: string;
    };
}

type Props = OwnProps;

export interface SurCarrerInfoStyles {
    container?: InjectableCss;
    ctacontainer?: InjectableCss;
    title?: InjectableCss;
    descStyles?: InjectableCss;
    ctaLink?: LinkProps;
    emailInfo?: InjectableCss;
}
export const surCarrerInfoStyles: SurCarrerInfoStyles = {
    container: {
        css: css`
            padding: 52px 0px 86px 0px;
            max-width: 955px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            row-gap: 50px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 20px;
                    `,
                    css`
                        padding: 20px;
                    `,
                    css`
                        padding: 20px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    title: {
        css: css`
            font-family: "Mulish";
            font-style: normal;
            font-weight: 300;
            font-size: 28px;
            line-height: 28px;
            text-align: center;
            color: ${getColor("text.main")};
        `,
    },
    descStyles: {
        css: css`
            font-family: "Mulish";
            font-style: normal;
            font-weight: 400;
            font-size: 15px;
            line-height: 24px;
            text-align: center;
            color: ${getColor("text.main")};
        `,
    },
    ctacontainer: {
        css: css`
            text-align: center;
            margin-top: 30px;
            padding: 10px 34px;
            background: ${getColor("primary")};
        `,
    },
};

const styles = surCarrerInfoStyles;

const SurCareerInfo: React.FC<Props> = ({ fields, id }) => {
    const { url } = useGetLink(fields.link);

    const ctaLink = {
        css: css`
            outline: 0;
            background-color: ${fields.backgroundColor};
            /* border: 1px solid ${getColor("primary")}; */
            height: 44px;
            padding: 10px 34px;
            width: max-content;
            max-width: 100%;
            display: block;
            margin: 0 auto;
            span {
                color: ${getColor("text.main")} !important;
                display: block;
                text-align: center;
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-weight: ${TYPOGRAPHY.weight.bold};
                color: ${getColor("primary.contrast")} !important;
                font-size: ${TYPOGRAPHY.size.base};
                line-height: ${TYPOGRAPHY.lineHeight.xbase};
            }
        `,
    };

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.title}>{fields.title}</StyledWrapper>
            <StyledWrapper {...styles.descStyles}>{fields.desc}</StyledWrapper>
            {fields.emailInfo && (
                <StyledWrapper {...styles.emailInfo}>{parse(fields.emailInfo, parserOptions)}</StyledWrapper>
            )}
            {fields.label && fields.link.value ? (
                <Link href={url} target={fields.openInNewWidow ? `_blank` : `_self`} {...ctaLink}>
                    {fields.label}
                </Link>
            ) : null}
        </StyledWrapper>
    );
};

const SurCustomInfoContentWidget: WidgetModule = {
    component: SurCareerInfo,
    definition: {
        group: "Common",
        displayName: "SurCareerInfo",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.desc,
                displayName: "Description",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.label,
                fieldType: "General",
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 0,
            },
            {
                fieldType: "Translatable",
                name: fields.emailInfo,
                editorTemplate: "RichTextField",
                displayName: "Email Info",
                defaultValue: "",
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.link,
                displayName: "Button Link",
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
            },
            {
                name: fields.openInNewWidow,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default SurCustomInfoContentWidget;
